import type { FilterConditionOutput } from '@shared/components/ui/AdvancedFilter';
import { useCallback, useEffect, useMemo, useState } from 'react';

interface ImportHistoryFiltersState {
  /** Un solo valor: '' = todos, o 'imported' | 'duplicated' | 'skipped' */
  status: string;
  shop_id: string[];
  /** Un solo valor: '' = todos, o 'automatic' | 'manual' */
  import_type: string;
}

interface UseImportHistoryFiltersProps {
  initialConditions?: FilterConditionOutput[];
  onChange?: (conditions: FilterConditionOutput[]) => void;
}

/**
 * Hook para gestionar los filtros del histórico de importaciones.
 * Convierte el estado UI a FilterConditionOutput[] para la API.
 */
export const useImportHistoryFilters = ({ initialConditions = [], onChange }: UseImportHistoryFiltersProps) => {
  const [filters, setFilters] = useState<ImportHistoryFiltersState>(() => parseConditionsToState(initialConditions));

  const convertStateToConditions = useCallback((state: ImportHistoryFiltersState): FilterConditionOutput[] => {
    const conditions: FilterConditionOutput[] = [];

    if (state.status) {
      conditions.push({
        column: 'status',
        action: 'in',
        value: [state.status],
      });
    }
    if (state.shop_id.length > 0) {
      conditions.push({
        column: 'shop_id',
        action: 'in',
        value: state.shop_id,
      });
    }
    if (state.import_type) {
      conditions.push({
        column: 'import_type',
        action: 'in',
        value: [state.import_type],
      });
    }

    return conditions;
  }, []);

  const updateFilters = useCallback(
    (updates: Partial<ImportHistoryFiltersState>) => {
      setFilters((prev) => {
        const newState = { ...prev, ...updates };
        onChange?.(convertStateToConditions(newState));
        return newState;
      });
    },
    [convertStateToConditions, onChange],
  );

  const handleStatusChange = useCallback((value: string) => updateFilters({ status: value }), [updateFilters]);
  const handleShopIdChange = useCallback((values: string[]) => updateFilters({ shop_id: values }), [updateFilters]);
  const handleImportTypeChange = useCallback((value: string) => updateFilters({ import_type: value }), [updateFilters]);

  const clearAllFilters = useCallback(() => {
    const emptyState: ImportHistoryFiltersState = {
      status: '',
      shop_id: [],
      import_type: '',
    };
    setFilters(emptyState);
    onChange?.([]);
  }, [onChange]);

  useEffect(() => {
    setFilters(parseConditionsToState(initialConditions));
  }, [initialConditions]);

  return {
    filters,
    handleStatusChange,
    handleShopIdChange,
    handleImportTypeChange,
    clearAllFilters,
  };
};

function parseConditionsToState(conditions: FilterConditionOutput[]): ImportHistoryFiltersState {
  const state: ImportHistoryFiltersState = {
    status: '',
    shop_id: [],
    import_type: '',
  };

  const getSingleValue = (column: 'status' | 'import_type'): string => {
    const condition = conditions.find((c) => c.column === column && c.action === 'in');
    if (!condition?.value) return '';
    const arr = Array.isArray(condition.value) ? condition.value : [String(condition.value)];
    return arr[0] ?? '';
  };

  const getArrayValue = (column: 'shop_id'): string[] => {
    const condition = conditions.find((c) => c.column === column && c.action === 'in');
    if (!condition?.value) return [];
    return Array.isArray(condition.value) ? condition.value : [String(condition.value)];
  };

  state.status = getSingleValue('status');
  state.shop_id = getArrayValue('shop_id');
  state.import_type = getSingleValue('import_type');

  return state;
}
